import React, { FunctionComponent } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import { composeValidators, fileValidation, requiredValidation } from 'helpers/validations';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';
import HebrewDateInput from 'components/inputs/HebrewDateInput';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';

import { IMatzevaFormData, IMatzevaFormProps } from './types';
import './i18n';

const MatzevaForm: FunctionComponent<IMatzevaFormProps> = (props: IMatzevaFormProps) => {
  const { cementeries, files, initialValues, isEditing, onSubmit } = props;

  const tabs = [
    i18next.t<string>('MATZEVA_FORM:PERSON'),
    i18next.t<string>('MATZEVA_FORM:CEMENTERY'),
    i18next.t<string>('MATZEVA_FORM:DETAILS'),
    i18next.t<string>('MATZEVA_FORM:APPLICANT'),
    i18next.t<string>('MATZEVA_FORM:COMMENTS'),
  ];

  return (
    <ViewContainer auth data-testid="matzeva-form">
      <Title>{i18next.t<string>('MATZEVA_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IMatzevaFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          <MultiStepForm.Page key="person-page">
            <Typography variant="h6">
              {i18next.t<string>('MATZEVA_FORM:PERSON_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('MATZEVA_FORM:LAST_NAME')}
                name="person.lastName"
                render={Input}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('MATZEVA_FORM:NAME')}
                name="person.name"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <Field
              label={i18next.t<string>('MATZEVA_FORM:MOTHER_NAME')}
              name="person.motherName"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('MATZEVA_FORM:DEATH_DATE')}
                name="person.deathDate"
                render={Input}
                type="date"
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('MATZEVA_FORM:DEATH_DATE_HEBREW')}
                name="person.deathDateHebrew"
                parse={(value) => value}
                render={HebrewDateInput}
                validate={requiredValidation}
              />
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="cementery-page">
            <Typography variant="h6">
              {i18next.t<string>('MATZEVA_FORM:CEMENTERY_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('MATZEVA_FORM:CEMENTERY')}
                name="cementery.cementery"
                options={cementeries}
                render={SelectInput}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('MATZEVA_FORM:LOCATION')}
                name="cementery.location"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('MATZEVA_FORM:SKETCH')}
                name="cementery.sketch"
                render={Input}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('MATZEVA_FORM:SKETCH_FILE')}
                name="cementery.sketchFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('MATZEVA_FORM:SKETCH_FILE'),
                    value: files?.cementerySketchFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('MATZEVA_FORM:SPANISH_FILE')}
                name="cementery.spanishFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('MATZEVA_FORM:SPANISH_FILE'),
                    value: files?.cementerySpanishFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <Field
                label={i18next.t<string>('MATZEVA_FORM:HEBREW_FILE')}
                name="cementery.hebrewFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('MATZEVA_FORM:HEBREW_FILE'),
                    value: files?.cementeryHebrewFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="details-page">
            <Typography variant="h6">
              {i18next.t<string>('MATZEVA_FORM:DETAILS_INFORMATION')}
            </Typography>
            <br />
            <Field
              label={i18next.t<string>('MATZEVA_FORM:MATZEVA_FILE')}
              name="details.matzevaFile"
              previewFiles={[
                {
                  label: i18next.t<string>('MATZEVA_FORM:MATZEVA_FILE'),
                  value: files?.detailsMatzevaFile,
                },
              ]}
              render={Input}
              type="file"
              validate={
                isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
              }
            />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('MATZEVA_FORM:MARBLE_WORKER_NAME')}
                name="details.marbleWorkerName"
                render={Input}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('MATZEVA_FORM:COLOR')}
                name="details.color"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('MATZEVA_FORM:MATERIALS')}
                name="details.materials"
                render={Input}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('MATZEVA_FORM:DIMENSIONS')}
                name="details.dimensions"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="applicant-page">
            <Typography variant="h6">
              {i18next.t<string>('MATZEVA_FORM:APPLICANT_INFORMATION')}
            </Typography>
            <br />
            <Field
              label={i18next.t<string>('MATZEVA_FORM:APPLICANT_NAME')}
              name="applicantName"
              render={Input}
              validate={requiredValidation}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('MATZEVA_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default MatzevaForm;
